import React, { useState } from 'react'
import {
  XMarkIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline'
import { paymentGatewayAPI } from '@/services/api'

interface CreateBoletoOrderModalProps {
  isOpen: boolean
  onClose: () => void
  gatewayId: string
  onSuccess?: () => void
}

interface OrderResult {
  order_id: string
  boleto_url: string
  boleto_barcode: string
  boleto_linha_digitavel: string
  due_date: string
  status: string
}

const CreateBoletoOrderModal: React.FC<CreateBoletoOrderModalProps> = ({
  isOpen,
  onClose,
  gatewayId,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [orderResult, setOrderResult] = useState<OrderResult | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  // Form state
  const [formData, setFormData] = useState({
    // Customer data
    customer_name: '',
    customer_email: '',
    customer_document: '',
    customer_type: 'individual',
    customer_phone_area: '',
    customer_phone_number: '',
    customer_address_line: '',
    customer_address_number: '',
    customer_address_zip: '',
    customer_address_city: '',
    customer_address_state: '',

    // Item data
    item_description: '',
    item_amount: '', // Em reais
    item_quantity: '1',

    // Boleto config
    due_date: '', // YYYY-MM-DD
    instructions: ''
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setLoading(true)

      // Converter valor de reais para centavos
      const amountInCents = Math.round(parseFloat(formData.item_amount) * 100)
      const quantity = parseInt(formData.item_quantity)

      // Preparar dados do pedido
      const orderData = {
        amount: amountInCents * quantity, // Total em centavos
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_document: formData.customer_document.replace(/\D/g, ''),
        customer_type: formData.customer_type,
        customer_phone_country: '55',
        customer_phone_area: formData.customer_phone_area,
        customer_phone_number: formData.customer_phone_number,
        customer_address_line: formData.customer_address_line,
        customer_address_number: formData.customer_address_number,
        customer_address_zip: formData.customer_address_zip.replace(/\D/g, ''),
        customer_address_city: formData.customer_address_city,
        customer_address_state: formData.customer_address_state,
        customer_address_country: 'BR',
        items: [
          {
            description: formData.item_description,
            amount: amountInCents,
            quantity: quantity
          }
        ],
        due_date: formData.due_date || undefined,
        instructions: formData.instructions || undefined
      }

      console.log('📤 Enviando pedido Boleto:', orderData)

      const result = await paymentGatewayAPI.createOrderBoleto(gatewayId, orderData)

      console.log('✅ Pedido Boleto criado:', result)

      setOrderResult(result)

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      console.error('❌ Erro ao criar pedido Boleto:', error)
      alert('Erro ao criar pedido Boleto: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100)
  }

  // Calcular data mínima para vencimento (hoje + 3 dias)
  const getMinDueDate = () => {
    const date = new Date()
    date.setDate(date.getDate() + 3)
    return date.toISOString().split('T')[0]
  }

  const handleClose = () => {
    setOrderResult(null)
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_document: '',
      customer_type: 'individual',
      customer_phone_area: '',
      customer_phone_number: '',
      customer_address_line: '',
      customer_address_number: '',
      customer_address_zip: '',
      customer_address_city: '',
      customer_address_state: '',
      item_description: '',
      item_amount: '',
      item_quantity: '1',
      due_date: '',
      instructions: ''
    })
    onClose()
  }

  if (!isOpen) return null

  // Se já temos resultado, mostra o boleto
  if (orderResult) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Boleto Gerado!</h2>
                  <p className="text-sm text-gray-500">ID: {orderResult.order_id}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Linha Digitável */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Linha Digitável
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={orderResult.boleto_linha_digitavel}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-gray-50"
                />
                <button
                  onClick={() => copyToClipboard(orderResult.boleto_linha_digitavel, 'linha')}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
                >
                  {copiedField === 'linha' ? (
                    <>
                      <CheckIcon className="w-5 h-5" />
                      Copiado!
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentIcon className="w-5 h-5" />
                      Copiar
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Código de Barras */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Código de Barras
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={orderResult.boleto_barcode}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-gray-50"
                />
                <button
                  onClick={() => copyToClipboard(orderResult.boleto_barcode, 'barcode')}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
                >
                  {copiedField === 'barcode' ? (
                    <>
                      <CheckIcon className="w-5 h-5" />
                      Copiado!
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentIcon className="w-5 h-5" />
                      Copiar
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Info */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className="font-semibold text-orange-700 uppercase">{orderResult.status}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Vencimento:</span>
                <span className="font-semibold text-gray-900">
                  {new Date(orderResult.due_date).toLocaleDateString('pt-BR')}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fechar
              </button>
              <a
                href={orderResult.boleto_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                Ver Boleto PDF
              </a>
            </div>

            {/* Instrução */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs text-blue-800">
                💡 <strong>Importante:</strong> O cliente pode pagar usando a linha digitável ou o código de barras em qualquer banco ou aplicativo de pagamento.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Form para criar pedido
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Criar Pedido - Boleto</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Dados do Cliente */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados do Cliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF/CNPJ *
                </label>
                <input
                  type="text"
                  name="customer_document"
                  value={formData.customer_document}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  DDD *
                </label>
                <input
                  type="text"
                  name="customer_phone_area"
                  value={formData.customer_phone_area}
                  onChange={handleChange}
                  required
                  maxLength={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone *
                </label>
                <input
                  type="text"
                  name="customer_phone_number"
                  value={formData.customer_phone_number}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endereço *
                </label>
                <input
                  type="text"
                  name="customer_address_line"
                  value={formData.customer_address_line}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Rua Exemplo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número *
                </label>
                <input
                  type="text"
                  name="customer_address_number"
                  value={formData.customer_address_number}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="123"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CEP *
                </label>
                <input
                  type="text"
                  name="customer_address_zip"
                  value={formData.customer_address_zip}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cidade *
                </label>
                <input
                  type="text"
                  name="customer_address_city"
                  value={formData.customer_address_city}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado *
                </label>
                <input
                  type="text"
                  name="customer_address_state"
                  value={formData.customer_address_state}
                  onChange={handleChange}
                  required
                  maxLength={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Dados do Item */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Item / Produto</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <input
                  type="text"
                  name="item_description"
                  value={formData.item_description}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Unitário (R$) *
                </label>
                <input
                  type="number"
                  name="item_amount"
                  value={formData.item_amount}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantidade *
                </label>
                <input
                  type="number"
                  name="item_quantity"
                  value={formData.item_quantity}
                  onChange={handleChange}
                  required
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total
                </label>
                <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg font-semibold text-gray-900">
                  {formData.item_amount && formData.item_quantity
                    ? `R$ ${(parseFloat(formData.item_amount) * parseInt(formData.item_quantity)).toFixed(2)}`
                    : 'R$ 0,00'}
                </div>
              </div>
            </div>
          </div>

          {/* Configurações do Boleto */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações do Boleto</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Vencimento (mínimo 3 dias)
                </label>
                <input
                  type="date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleChange}
                  min={getMinDueDate()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Deixe em branco para vencimento automático em 3 dias
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instruções (opcional)
                </label>
                <textarea
                  name="instructions"
                  value={formData.instructions}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Ex: Não receber após o vencimento"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Gerando...
                </>
              ) : (
                <>
                  <DocumentTextIcon className="w-5 h-5" />
                  Gerar Boleto
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateBoletoOrderModal
